import { Box, Button, Center, Flex, Stack, Text, VStack } from "@chakra-ui/react";
import { useMarkPaymentAsPaidMutation } from "apis";
import { ExchangeInfo, FormInfo, PaymentExchangeInfoDetail, QuoteTimer, Uploader } from "components";
import { usePartialState } from "hooks";
import { PayInRO } from "interfaces/pay.interface";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectForm } from "store/slices";
import { clearSession, readableStatus, transferStatusColor, uploadFile } from "utils";
import { PaySuccessView } from "components/Pay/PaySuccessView";

interface IState {
  receipt: File;
  isLoading: boolean;
}
interface IProps {
  data: PayInRO;
}
const PayWithBank = ({ data }: IProps) => {
  const [state, set] = usePartialState<IState>();
  const { timerExpired } = useSelector(selectForm);

  const [isSuccessView, setIsSuccessView] = useState(false);

  const [_markAsPaid] = useMarkPaymentAsPaidMutation();
  const handleContinue = async (e: any) => {
    e.preventDefault();
    if (!state?.receipt && !data?.reference) return;
    try {
      set({ isLoading: true });
      const upload = await uploadFile(state.receipt!, `${data?.paymentId}__${data?.reference!}__${Date.now()}`);

      if (upload.location && data?.paymentId) {
        await _markAsPaid({ paymentId: data.paymentId, paymentUrl: upload.location });

        set({ isLoading: false });

        setIsSuccessView(true);
      }
      set({ isLoading: false });
    } catch (error) {
      set({ isLoading: false });
    }
  };

  const isDisabled = useMemo(() => !state?.receipt || state?.isLoading || timerExpired, [state, timerExpired]);

  return (
    <Box>
      {!isSuccessView ? (
        <Stack as="form" onSubmit={handleContinue}>
          <Flex justifyContent="space-between">
            <Text fontWeight="500">Payment Details</Text>
          </Flex>

          <ExchangeInfo gridGap="10px">
            <PaymentExchangeInfoDetail title="Payment For:" description={data.order} />

            <PaymentExchangeInfoDetail
              title="Original Amount:"
              description={`${data?.unit?.toUpperCase()} ${Number(data?.amount!).toLocaleString()}` ?? ""}
            />

            <PaymentExchangeInfoDetail
              title="Transaction Fee:"
              description={`${data?.unit?.toUpperCase()} ${Number(data?.fee).toLocaleString()}` ?? ""}
            />

            <PaymentExchangeInfoDetail
              title="Total Amount:"
              description={`${data?.unit?.toUpperCase()} ${Number(data?.totalAmount).toLocaleString()}` ?? ""}
            />

            <PaymentExchangeInfoDetail canCopy title="Payment ID:" description={data.paymentId} />
          </ExchangeInfo>

          <ExchangeInfo mt="16px !important">
            <PaymentExchangeInfoDetail canCopy title="Bank name:" description={data?.paymentAccountData?.networkOrBankCode ?? ""} />

            <PaymentExchangeInfoDetail canCopy title="Account name:" description={data?.paymentAccountData?.accountName ?? ""} />

            <PaymentExchangeInfoDetail
              canCopy
              title="Account Number:"
              description={data?.paymentAccountData?.accountNumber ?? ""}
            />

            <PaymentExchangeInfoDetail canCopy title="Transaction reference:" description={data?.reference ?? ""} />
          </ExchangeInfo>

          <Flex gridGap="8px" fontSize="14px" pl="4px">
            <Text>Payment Status:</Text>

            <Text textTransform="capitalize" color={transferStatusColor(data.paymentStatus)}>
              {readableStatus(data?.paymentStatus)}
            </Text>
          </Flex>

          <>
            {data.paymentStatus === "pending" && (
              <>
                <QuoteTimer
                  title="Payment should be made within"
                  mt="32px !important"
                  time={new Date(data?.guaranteedUntil).toISOString()}
                  checkExpire
                />

                <FormInfo
                  info=""
                  isInvalid
                  mt="12px !important"
                  description="PS: Kindly avoid using any crypto-related keywords in your payment remarks"
                  _description={{ textAlign: "center" }}
                />

                <VStack mt="28px !important">
                  <Uploader label="Upload Payment Receipt" onFiles={(files) => set({ receipt: files[0] })} />
                </VStack>

                <VStack mt="40px !important">
                  <Button
                    w="100%"
                    minW={{ base: "auto", "2sm": "400px" }}
                    type="submit"
                    isDisabled={isDisabled}
                    isLoading={!!state?.isLoading}
                    textTransform="initial"
                    fontFamily="var(--bitmama-fonts-heading)"
                  >
                    I have Paid
                  </Button>

                  <Button
                    variant="transparent"
                    w="100%"
                    onClick={clearSession}
                    fontWeight="500"
                    _focus={{ shadow: "outline", textDecoration: "underline" }}
                  >
                    Cancel Payment
                  </Button>
                </VStack>
              </>
            )}
          </>
        </Stack>
      ) : (
        <Center width="100%">
          <PaySuccessView set={set} state={state} id={data.paymentId} />
        </Center>
      )}
    </Box>
  );
};

export default PayWithBank;
